import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatErr } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, object=authed
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      // Silence the expected 401 from initial unauthenticated check
      const { data } = await api.get("/auth/me", { validateStatus: (s) => s < 500 });
      if (data && data.id) setUser(data);
      else setUser(false);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return true;
    } catch (e) {
      // Surface the most useful error message we can — server detail first, then HTTP-level message,
      // then a generic fallback. This is critical on mobile where network/cookie issues are common.
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      const msg =
        (detail && formatErr(detail)) ||
        (status === 401 ? "Invalid email or password." : null) ||
        (e.code === "ERR_NETWORK" ? "Cannot reach server. Check your connection and try again." : null) ||
        e.message ||
        "Something went wrong. Please try again.";
      setError(msg);
      return false;
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
